(() => {
  if (customElements.get('quantity-breaks')) return;

  class QuantityBreaks extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.updatePrices = this.dataset.updatePrices === 'true';
      this.moneyFormat =
        this.dataset.moneyFormat ||
        (window.theme && window.theme.settings && window.theme.settings.money_format) ||
        '${{amount}}';

      // Variants JSON is rendered at the bottom of the snippet.
      const variantsScript = this.querySelector('script[type="application/json"]');
      try {
        this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
      } catch (e) {
        this.variants = [];
      }

      this.priceContainer = document.querySelector(`#price-${this.sectionId} .price__container`);
      this.atcPriceEl = document.querySelector(`#main-atc-price-${this.sectionId}`);

      if (this.priceContainer) {
        this.basePrice = parseInt(this.priceContainer.dataset.price, 10);
        this.originalPriceHTML = this.priceContainer.innerHTML;
      }
      if (this.atcPriceEl) {
        this.originalAtcHTML = this.atcPriceEl.innerHTML;
      }

      // Listen to break (radio) changes.
      this.querySelectorAll('input[type="radio"][name="quantity"]').forEach(r => {
        r.addEventListener('change', () => this.onSelectionChange());
      });

      // Listen to per-item variant selector changes.
      this.querySelectorAll('.quantity-break__variant-select').forEach(s => {
        s.addEventListener('change', () => this.onSelectionChange());
      });

      // Locate main product form.
      this.mainForm =
        document.querySelector(`#product-form-${this.sectionId}`) ||
        document.querySelector(`form[id^="product-form-${this.sectionId}"]`);

      // Use a capture-phase listener on document so we run before the
      // existing <product-form> submit handler (which is on the form itself).
      this.boundSubmitHandler = this.onFormSubmit.bind(this);
      document.addEventListener('submit', this.boundSubmitHandler, true);

      this.onSelectionChange();
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this.boundSubmitHandler, true);
    }

    /* ---------- selection helpers ---------- */

    getActiveLabel() {
      const checked = this.querySelector('input[type="radio"][name="quantity"]:checked');
      if (!checked) return null;
      return this.querySelector(`label[for="${checked.id}"]`);
    }

    getMainSelectedVariant() {
      const sel = document.querySelector(
        `#product-variant-selects-${this.sectionId} script[data-selected-variant]`
      );
      if (sel) {
        try {
          return JSON.parse(sel.textContent);
        } catch (e) {}
      }
      const idInput = this.mainForm && this.mainForm.querySelector('input[name="id"]');
      if (idInput) {
        const id = parseInt(idInput.value, 10);
        return this.variants.find(v => v.id === id) || null;
      }
      return this.variants[0] || null;
    }

    findVariantByOptions(optionValues) {
      return this.variants.find(v =>
        optionValues.every((val, i) => v.options[i] === val)
      );
    }

    // Returns an array (length = quantity) of variant objects for the active break.
    // Falls back to repeating the main selected variant when no per-item pickers exist.
    getSelectedVariantsForActiveBreak() {
      const label = this.getActiveLabel();
      if (!label) return [];
      const quantity = parseInt(label.dataset.quantity, 10) || 1;

      const items = label.querySelectorAll('.quantity-break__selector-item');
      if (items.length === 0) {
        const main = this.getMainSelectedVariant();
        return main ? Array(quantity).fill(main) : [];
      }

      return Array.from(items).map(item => {
        const selects = item.querySelectorAll('select');
        const optionValues = Array.from(selects).map(s => s.value);
        return this.findVariantByOptions(optionValues) || this.getMainSelectedVariant();
      });
    }

    /* ---------- price display ---------- */

    onSelectionChange() {
      if (!this.updatePrices) return;
      if (!this.priceContainer || isNaN(this.basePrice)) return;

      const label = this.getActiveLabel();
      if (!label) return;

      const quantity = parseInt(label.dataset.quantity, 10) || 1;
      const percentageLeft = parseFloat(label.dataset.percentageLeft);
      const fixedDiscount = parseFloat(label.dataset.fixedDiscount) || 0;

      // Sum the actual selected variants' prices so bundles with mixed variants
      // reflect the real subtotal (the "bundle & save" case).
      const variants = this.getSelectedVariantsForActiveBreak();
      let subtotal = 0;
      if (variants.length === quantity && variants.every(v => v && typeof v.price === 'number')) {
        subtotal = variants.reduce((sum, v) => sum + v.price, 0);
      } else {
        const main = this.getMainSelectedVariant();
        subtotal = (main ? main.price : this.basePrice) * quantity;
      }

      const discountedTotal = Math.max(
        0,
        Math.round(subtotal * percentageLeft - fixedDiscount)
      );
      const discountedEach = Math.round(discountedTotal / quantity);
      const baseEach = Math.round(subtotal / quantity);
      const isDiscounted = discountedEach < baseEach;

      const formattedNew = formatPrice(discountedEach, this.moneyFormat);
      const formattedBase = formatPrice(baseEach, this.moneyFormat);

      this.renderPrice(formattedNew, formattedBase, isDiscounted);

      if (this.atcPriceEl) {
        this.atcPriceEl.innerHTML = `${formattedNew} •`;
      }
    }

    renderPrice(newPriceHTML, basePriceHTML, isDiscounted) {
      if (!this.priceContainer) return;
      this.priceContainer.innerHTML = this.originalPriceHTML;

      const regularEls = this.priceContainer.querySelectorAll(
        '.price__regular span:not(.visually-hidden)'
      );
      const saleIns = this.priceContainer.querySelector(
        '.price__sale-inner ins ins, .price__sale-inner > ins'
      );
      const saleCompare = this.priceContainer.querySelector('.price__sale-inner s');

      if (isDiscounted) {
        const priceWrapper = this.priceContainer.closest('.price');
        if (priceWrapper) priceWrapper.classList.add('price--on-sale');

        if (saleIns) saleIns.innerHTML = newPriceHTML;
        regularEls.forEach(el => (el.innerHTML = newPriceHTML));

        if (saleCompare) {
          saleCompare.innerHTML = basePriceHTML;
        } else if (regularEls.length) {
          const sEl = document.createElement('s');
          sEl.className = 'price__compare-injected';
          sEl.style.marginLeft = '6px';
          sEl.innerHTML = basePriceHTML;
          regularEls[regularEls.length - 1].insertAdjacentElement('afterend', sEl);
        }
      } else {
        regularEls.forEach(el => (el.innerHTML = newPriceHTML));
        if (saleIns) saleIns.innerHTML = newPriceHTML;
      }
    }

    /* ---------- form submit interception ---------- */

    async onFormSubmit(event) {
      if (!this.mainForm || event.target !== this.mainForm) return;

      const label = this.getActiveLabel();
      if (!label) return; // no break selected, let normal flow run

      const variants = this.getSelectedVariantsForActiveBreak();
      if (!variants.length || variants.some(v => !v)) return;

      // Stop the existing product-form handler from running.
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();

      // Aggregate quantities by variant id so identical picks become one line item.
      const counts = {};
      variants.forEach(v => {
        counts[v.id] = (counts[v.id] || 0) + 1;
      });
      const items = Object.entries(counts).map(([id, qty]) => ({
        id: parseInt(id, 10),
        quantity: qty
      }));

      const submitButton = this.mainForm.querySelector('[type="submit"]');
      const cartTarget =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer');

      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
      }

      try {
        const body = { items };
        if (cartTarget && typeof cartTarget.getSectionsToRender === 'function') {
          body.sections = cartTarget.getSectionsToRender().map(s => s.section);
          body.sections_url = window.location.pathname;
          if (typeof cartTarget.setActiveElement === 'function') {
            cartTarget.setActiveElement(document.activeElement);
          }
        }

        const response = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        });
        const data = await response.json();

        if (data.status) {
          console.error('quantity-breaks add error:', data.description || data.message);
          return;
        }

        if (cartTarget && typeof cartTarget.renderContents === 'function' && data.sections) {
          cartTarget.renderContents(data);
        }
        if (typeof updateCartCounters === 'function') updateCartCounters();
      } catch (err) {
        console.error('quantity-breaks add failed', err);
      } finally {
        if (submitButton) {
          submitButton.removeAttribute('aria-disabled');
          submitButton.classList.remove('loading');
        }
      }
    }
  }

  customElements.define('quantity-breaks', QuantityBreaks);
})();
